import type React from 'react'
import { type KeyboardEvent, useRef, useState } from 'react'
import { v7 as uuidv7 } from 'uuid'
import { CloseIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import {
	type BaseFormElementProps,
	type FormElementStyleProps,
	FormElementWrapper,
	formStyles,
} from './FormElementWrapper'

export interface Tag {
	id: string
	label: string
}

interface EditableTagFieldProps extends BaseFormElementProps {
	value?: Tag[]
	inputValue?: string
	defaultValue?: Tag[]
	maxTags?: number
	onChange?: (tags: Tag[]) => void
	onChangeText?: (text: string) => void
	placeholder?: string
	readonly?: boolean
	enterEnabled?: boolean
	onAddTag?: (label: string) => Tag | null
}

const tagFieldStyles = {
	tag: cva('flex items-center gap-1 bg-neutral-100 text-neutral-800 py-1 px-2 rounded-sm text-sm max-w-full', {
		variants: {
			disabled: {
				true: 'bg-neutral-100 text-neutral-400',
				false: '',
			},
		},
		defaultVariants: {
			disabled: false,
		},
	}),
	tagLabel: 'truncate',
	input: 'outline-none border-none bg-transparent focus:ring-0 text-sm',
}

export const EditableTagField: React.FC<EditableTagFieldProps> = ({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	className,
	maxTags = Number.MAX_SAFE_INTEGER,
	value: controlledTags,
	inputValue: controlledInputValue,
	defaultValue = [],
	onChange,
	onChangeText,
	placeholder = 'Add tag...',
	isRequired = false,
	readonly = false,
	enterEnabled = true,
	onAddTag,
}) => {
	const [innerStateInputValue, setInputValue] = useState('')
	const [isFocused, setIsFocused] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const [tags, setTags] = useState<Tag[]>(defaultValue)

	const inputValue = controlledInputValue !== undefined ? controlledInputValue : innerStateInputValue
	const currentTags = controlledTags !== undefined ? controlledTags : tags

	if (currentTags.length >= maxTags && inputValue.length > 0) {
		setInputValue('')
		onChangeText?.('')
	}

	const resolvedVariant =
		variant === 'error' ? 'error' : currentTags.length > 0 || isFocused || inputValue.length > 0 ? 'filled' : 'default'

	const styleProps: FormElementStyleProps = {
		variant: resolvedVariant,
		disabled,
		isFocused,
		hasValue: currentTags.length > 0 || inputValue.length > 0,
	}

	const handleAddTag = (tagText: string) => {
		tagText = tagText.trim()
		if (!tagText) return

		if (currentTags.length >= maxTags) {
			return
		}

		let newTag: Tag

		if (onAddTag) {
			const customTag = onAddTag(tagText)
			if (!customTag) return
			newTag = customTag
		} else {
			newTag = {
				id: uuidv7(),
				label: tagText,
			}
		}

		const updatedTags = [...currentTags, newTag]

		if (controlledTags === undefined) {
			setTags(updatedTags)
		}

		if (onChange) {
			onChange(updatedTags)
		}

		setInputValue('')
		onChangeText?.('')
	}

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && enterEnabled) {
			e.preventDefault()
			handleAddTag(inputValue)
		}
	}

	const handleRemoveTag = (tagId: string) => {
		const updatedTags = currentTags.filter((tag) => tag.id !== tagId)

		if (controlledTags === undefined) {
			setTags(updatedTags)
		}

		if (onChange) {
			onChange(updatedTags)
		}
	}

	const renderTagInput = () => {
		return (
			// biome-ignore lint/a11y/useSemanticElements: tag input group with dynamic styling
			<div
				role='group'
				className={cn(
					formStyles.element({
						variant: styleProps.variant,
						isFocused: styleProps.isFocused,
						disabled: styleProps.disabled,
						readOnly: readonly,
					}),
					'flex flex-wrap items-center gap-2 bg-neutrals-0',
					className,
				)}
				onClick={() => {
					if (!disabled && !readonly && inputRef.current) {
						inputRef.current.focus()
					}
				}}
				onKeyDown={(e) => {
					if ((e.key === 'Enter' || e.key === ' ') && !disabled && !readonly && inputRef.current) {
						inputRef.current.focus()
					}
				}}>
				{currentTags.map((tag) => (
					<div
						key={tag.id}
						className={tagFieldStyles.tag({
							disabled: styleProps.disabled,
						})}>
						<span className={tagFieldStyles.tagLabel} title={tag.label}>
							{tag.label}
						</span>
						{!styleProps.disabled && !readonly && (
							<button
								type='button'
								onClick={(e) => {
									e.stopPropagation()
									handleRemoveTag(tag.id)
								}}
								className='focus:outline-none bg-neutral-200 hover:bg-neutral-300 rounded-sm cursor-pointer flex-shrink-0'>
								<CloseIcon size={16} />
							</button>
						)}
					</div>
				))}

				{!disabled && !readonly && (
					<input
						ref={inputRef}
						type='text'
						className={cn(tagFieldStyles.input, 'w-full')}
						value={inputValue}
						onChange={(e) => {
							if (currentTags.length >= maxTags) {
								return
							}

							onChangeText?.(e.target.value)
							setInputValue(e.target.value)
						}}
						onKeyDown={handleKeyDown}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						placeholder={currentTags.length === 0 && inputValue === '' ? placeholder : ''}
					/>
				)}

				{currentTags.length === 0 && inputValue === '' && readonly && (
					<span className='text-neutral-500'>{placeholder}</span>
				)}
			</div>
		)
	}

	if (readonly) {
		return (
			<div
				className={cn(
					formStyles.element({
						variant: styleProps.variant,
						isFocused: false,
						disabled: styleProps.disabled,
						readOnly: true,
					}),
					'flex flex-wrap items-center gap-2 bg-neutrals-0',
					className,
				)}>
				{currentTags.map((tag) => (
					<div
						key={tag.id}
						className={tagFieldStyles.tag({
							disabled: styleProps.disabled,
						})}>
						<span className={tagFieldStyles.tagLabel} title={tag.label}>
							{tag.label}
						</span>
					</div>
				))}
				{currentTags.length === 0 && <span className='text-neutral-500'>{placeholder}</span>}
			</div>
		)
	}

	return (
		<FormElementWrapper
			label={label}
			hint={hint}
			showHintIcon={showHintIcon}
			variant={variant}
			disabled={disabled}
			isRequired={isRequired}
			styleProps={styleProps}>
			{renderTagInput()}
		</FormElementWrapper>
	)
}
